import { useState, useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faCheck } from '@fortawesome/free-solid-svg-icons';
import Button from './Button';
import { LangContext, LangContextType, ToastContext, ToastContextType, LoadingContext, LoadingContextType } from '../utilities/customHooks';
import instance from '../middleware/HTTPInterceptors';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";

interface EmailVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  userEmail: string;
}

type ModalState = 'initial' | 'loading' | 'success' | 'error';

function EmailVerificationModal({ isOpen, onClose, userEmail }: EmailVerificationModalProps) {
  const { langState } = useContext(LangContext) as LangContextType;
  const { showToast } = useContext(ToastContext) as ToastContextType;
  const { showLoading, hideLoading } = useContext(LoadingContext) as LoadingContextType;

  const [email, setEmail] = useState(userEmail);
  const [state, setState] = useState<ModalState>('initial');
  const [errorMessage, setErrorMessage] = useState('');

  const handleResendVerification = async () => {
    if (!email) {
      setErrorMessage(langState === 'fr'
        ? 'Veuillez entrer votre adresse email'
        : 'Please enter your email address'
      );
      return;
    }

    setState('loading');
    setErrorMessage('');
    showLoading();

    try {
      const response = await instance.post('/api/auth/resend-verification', { email });

      hideLoading();

      if (response.data.success) {
        setState('success');
        showToast({
          toastType: 'success',
          message: langState === 'fr'
            ? 'Email de vérification envoyé! Vérifiez votre boîte de réception.'
            : 'Verification email sent! Check your inbox.',
          autoDisappearTimeInSeconds: 5,
        });

        // Auto-close modal after 2 seconds on success
        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        setState('error');
        setErrorMessage(response.data.message || (langState === 'fr'
          ? "Impossible d'envoyer l'email"
          : 'Failed to send email'));
      }
    } catch (error: unknown) {
      hideLoading();
      setState('error');
      const axiosErr = error as { response?: { data?: { message?: string }; status?: number } };

      if (axiosErr.response?.status === 429) {
        setErrorMessage(langState === 'fr'
          ? 'Trop de demandes. Veuillez réessayer plus tard.'
          : 'Too many requests. Please try again later.');
      } else if (axiosErr.response?.status === 400 && axiosErr.response?.data?.message?.includes('already verified')) {
        showToast({
          toastType: 'success',
          message: langState === 'fr'
            ? 'Cet email est déjà vérifié. Vous pouvez vous connecter.'
            : 'This email is already verified. You can refresh the page.',
          autoDisappearTimeInSeconds: 5,
        });
        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        setErrorMessage(langState === 'fr'
          ? "Une erreur s'est produite. Veuillez réessayer."
          : 'An error occurred. Please try again.');
      }
    }
  };

  const handleClose = () => {
    setState('initial');
    setErrorMessage('');
    setEmail(userEmail); // Reset to original email
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent>
        <DialogTitle className="text-xl font-semibold text-primaryDark">
          {langState === 'fr' ? 'Vérifier votre email' : 'Verify Your Email'}
        </DialogTitle>

        {/* Content */}
        <div className="flex flex-col space-y-4">
          {state === 'success' ? (
            <div className="flex flex-col items-center py-8 space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <FontAwesomeIcon icon={faCheck} className="text-cGreen" size="2x" />
              </div>
              <p className="text-center text-cGreen font-semibold">
                {langState === 'fr'
                  ? 'Email envoyé avec succès!'
                  : 'Email sent successfully!'}
              </p>
            </div>
          ) : (
            <>
              <p className="text-gray-700">
                {langState === 'fr'
                  ? "Cliquez sur le bouton ci-dessous pour envoyer un lien de vérification à:"
                  : "Click the button below to send a verification link to:"}
              </p>
              <p className="font-semibold text-primary">{userEmail}</p>

              <p className="text-sm text-gray-600">
                {langState === 'fr'
                  ? "Ensuite, vérifiez votre boîte de réception et cliquez sur le lien pour activer votre compte."
                  : "Then check your inbox and click the link to activate your account."}
              </p>

              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <p className="text-sm font-medium text-gray-700">
                  {langState === 'fr'
                    ? "Vous n'avez pas reçu l'email?"
                    : "Didn't receive the email?"}
                </p>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li>• {langState === 'fr' ? 'Vérifiez votre dossier spam' : 'Check your spam folder'}</li>
                  <li>• {langState === 'fr' ? "Le lien expire après 24 heures" : "The link expires after 24 hours"}</li>
                </ul>
              </div>

              {/* Email input (editable if needed) */}
              <div>
                <label htmlFor="email-input" className="block text-sm font-medium text-gray-700 mb-2">
                  {langState === 'fr' ? 'Email' : 'Email'}
                </label>
                <input
                  id="email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder={langState === 'fr' ? 'Entrez votre email' : 'Enter your email'}
                />
              </div>

              {/* Error message */}
              {errorMessage && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                  <p className="text-sm text-red-700">{errorMessage}</p>
                </div>
              )}

              {/* Resend button */}
              <Button
                buttonText={
                  state === 'loading'
                    ? (langState === 'fr' ? 'Envoi en cours...' : 'Sending...')
                    : (langState === 'fr' ? 'Envoyer l\'email de vérification' : 'Send Verification Email')
                }
                buttonType="primaryFull"
                actionHandler={handleResendVerification}
                disabled={state === 'loading'}
              />

              {/* "I'll do this later" link */}
              <div className="text-center">
                <button
                  onClick={handleClose}
                  className="text-sm text-gray-600 hover:text-primary transition underline"
                >
                  {langState === 'fr' ? 'Je le ferai plus tard' : "I'll do this later"}
                </button>
              </div>

              <p className="text-xs text-gray-400 text-center">
                {langState === 'fr'
                  ? 'Limité à 3 demandes par heure'
                  : 'Limited to 3 requests per hour'}
              </p>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default EmailVerificationModal;
